import { ConditionComparatorEnum, WhereClauseCondition } from "../ars_ldm";

// Comparator map: keys are possible tokens found in code (symbols or names).
export const comparatorDefinitions: {
  [key: string]: {
    enum: ConditionComparatorEnum;
    write: string;
    read: string[];
  };
} = {
  EQ: {
    enum: ConditionComparatorEnum.EQ,
    write: "=",
    read: ["EQ", "="],
  },
  NE: {
    enum: ConditionComparatorEnum.NE,
    write: "!=",
    read: ["NE", "!=", "<>"],
  },
  GT: {
    enum: ConditionComparatorEnum.GT,
    write: ">",
    read: ["GT", ">"],
  },
  GE: {
    enum: ConditionComparatorEnum.GE,
    write: ">=",
    read: ["GE", ">="],
  },
  LT: {
    enum: ConditionComparatorEnum.LT,
    write: "<",
    read: ["LT", "<"],
  },
  LE: {
    enum: ConditionComparatorEnum.LE,
    write: "<=",
    read: ["LE", "<="],
  },
  IN: {
    enum: ConditionComparatorEnum.IN,
    write: "IN",
    read: ["IN"],
  },
  NOTIN: {
    enum: ConditionComparatorEnum.NOTIN,
    write: "NOT IN",
    read: ["NOTIN", "NOT IN"],
  },
};

/**
 * Builds a regex pattern group from read strings, escaping special regex characters.
 * Sorts by length (descending) to ensure longer patterns like "NOT IN" match before "IN".
 *
 * @param readStrings - Array of strings that should match this comparator.
 * @returns Escaped pattern string for use in regex alternation.
 */
const buildComparatorPattern = (readStrings: string[]): string => {
  return readStrings
    .slice()
    .sort((a, b) => b.length - a.length)
    .map((s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"))
    .join("|");
};

/**
 * Finds the comparator definition that matches the given token.
 *
 * @param token - The comparator token to look up.
 * @returns The matching comparator definition or undefined.
 */
const findComparatorDef = (
  token: string,
): { enum: ConditionComparatorEnum; write: string; read: string[] } | undefined => {
  const upperToken = token.toUpperCase();
  return Object.values(comparatorDefinitions).find((def) =>
    def.read.map((r) => r.toUpperCase()).includes(upperToken),
  );
};

/**
 * Parses a where clause code string into a WhereClauseCondition object.
 *
 * @param code - The code string to parse (e.g., "ADSL.SAFFL = 'Y'").
 * @param availableVariables - List of available dataset.variable combinations for validation.
 * @returns The parsed WhereClauseCondition or false if parsing fails.
 */
export const parseConditionFromCode = (
  code: string,
  availableVariables: string[],
): WhereClauseCondition | false => {
  if (!code || code.trim() === "") {
    return false;
  }

  const trimmed = code.trim();

  // Build regex patterns from comparator definitions
  const singleValueComparators: string[] = [];
  const listValueComparators: string[] = [];

  Object.values(comparatorDefinitions).forEach((def) => {
    const pattern = buildComparatorPattern(def.read);
    if (def.enum === ConditionComparatorEnum.IN || def.enum === ConditionComparatorEnum.NOTIN) {
      listValueComparators.push(pattern);
    } else {
      singleValueComparators.push(pattern);
    }
  });

  // Sort patterns by length (descending) to match longer patterns first
  const sortedSingleValueComparators = singleValueComparators.sort((a, b) => b.length - a.length);
  const sortedListValueComparators = listValueComparators.sort((a, b) => b.length - a.length);

  // Allow optional spaces around comparators (e.g., ADSL.SAFFL='Y' or ADSL.SAFFL = 'Y')
  const singleValuePattern = new RegExp(
    `^([A-Z0-9_]+)\\.([A-Z0-9_]+)\\s*(${sortedSingleValueComparators.join("|")})\\s*(?:'([^']*)'|([^\\s]+))$`,
    "i",
  );
  const inPattern = new RegExp(
    `^([A-Z0-9_]+)\\.([A-Z0-9_]+)\\s*(${sortedListValueComparators.join("|")})\\s*\\(([^)]+)\\)$`,
    "i",
  );

  let match = trimmed.match(singleValuePattern);
  if (match) {
    const [, dataset, variable, comparator, quotedValue, unquotedValue] = match;
    const datasetVariable = `${dataset.toUpperCase()}.${variable.toUpperCase()}`;

    // Validate that the variable exists
    if (!availableVariables.map((v) => v.toUpperCase()).includes(datasetVariable)) {
      return false;
    }

    const value = quotedValue !== undefined ? quotedValue : unquotedValue;
    const def = findComparatorDef(comparator);

    return def
      ? {
          dataset: dataset.toUpperCase(),
          variable: variable.toUpperCase(),
          comparator: def.enum,
          value: [value],
        }
      : // istanbul ignore next
        false;
  }

  match = trimmed.match(inPattern);
  if (match) {
    const [, dataset, variable, comparator, valuesString] = match;
    const datasetVariable = `${dataset.toUpperCase()}.${variable.toUpperCase()}`;

    // Validate that the variable exists
    if (!availableVariables.map((v) => v.toUpperCase()).includes(datasetVariable)) {
      return false;
    }

    // Parse values from the IN clause
    const values = valuesString
      .split(",")
      .map((v) => v.trim())
      .filter((v) => v.length > 0)
      .map((v) => {
        // Remove quotes if present
        if ((v.startsWith("'") && v.endsWith("'")) || (v.startsWith('"') && v.endsWith('"'))) {
          return v.slice(1, -1);
        }
        return v;
      });

    if (values.length === 0) {
      return false;
    }

    const def = findComparatorDef(comparator);
    // istanbul ignore next - defensive check, should never fail since regex is built from definitions

    return def
      ? {
          dataset: dataset.toUpperCase(),
          variable: variable.toUpperCase(),
          comparator: def.enum,
          value: values,
        }
      : // istanbul ignore next
        false;
  }
  // No match found
  return false;
};
